import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import axios from 'axios';

const DEFAULT_LIMIT = 50;

function IconPlus() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
    </svg>
  );
}

function IconSearch() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
    </svg>
  );
}

function IconChevronLeft() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="15 18 9 12 15 6"/>
    </svg>
  );
}

function IconChevronRight() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="9 18 15 12 9 6"/>
    </svg>
  );
}

function IconTrash() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="3 6 5 6 21 6"/>
      <path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/>
      <path d="M10 11v6"/><path d="M14 11v6"/>
      <path d="M9 6V4h6v2"/>
    </svg>
  );
}

export default function ProductList() {
  const navigate = useNavigate();

  const [searchValue, setSearchValue] = useState('');
  const [activeQ, setActiveQ]         = useState('');
  const [rows, setRows]               = useState([]);
  const [total, setTotal]             = useState(0);
  const [page, setPage]               = useState(1);
  const [pages, setPages]             = useState(1);
  const [loading, setLoading]         = useState(false);
  const [error, setError]             = useState('');
  const [selectedIds, setSelectedIds]             = useState([]);
  const [allResultsSelected, setAllResultsSelected] = useState(false);
  const [bulkDeleting, setBulkDeleting]           = useState(false);

  const gridRef = useRef();

  const fetchData = useCallback(async (q, pageNum) => {
    setLoading(true);
    setError('');
    try {
      const params = { page: pageNum, limit: DEFAULT_LIMIT };
      if (q) params.q = q;
      const res = await axios.get('/products', { params });
      setRows(res.data.data);
      setTotal(res.data.total);
      setPages(res.data.pages);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to load products');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData('', 1); }, [fetchData]);

  function handleSearch() {
    setActiveQ(searchValue.trim());
    setPage(1);
    fetchData(searchValue.trim(), 1);
  }

  function handleClear() {
    setSearchValue('');
    setActiveQ('');
    setPage(1);
    fetchData('', 1);
  }

  function handleKeyDown(e) {
    if (e.key === 'Enter') handleSearch();
  }

  function handlePageChange(newPage) {
    setPage(newPage);
    fetchData(activeQ, newPage);
  }

  function handleDelete(id, name) {
    if (!window.confirm(`Delete product "${name}"? Features assigned to it will become unassigned.`)) return;
    axios.delete(`/products/${id}`)
      .then(() => fetchData(activeQ, page))
      .catch(e => alert(e.response?.data?.error || 'Delete failed'));
  }

  function handleSelectionChanged() {
    const selected = gridRef.current?.api?.getSelectedRows() || [];
    setSelectedIds(selected.map(r => r.id));
    if (selected.length < rows.length) setAllResultsSelected(false);
  }

  async function handleBulkDelete() {
    if (allResultsSelected) {
      await handleDeleteAllResults();
      return;
    }
    if (!selectedIds.length) return;
    if (!window.confirm(
      `Delete ${selectedIds.length} selected product${selectedIds.length !== 1 ? 's' : ''}?\n\nFeatures assigned to them will become unassigned. This cannot be undone.`
    )) return;
    setBulkDeleting(true);
    try {
      await axios.post('/products/bulk-delete', { ids: selectedIds });
      setSelectedIds([]);
      setAllResultsSelected(false);
      gridRef.current?.api?.deselectAll();
      await fetchData(activeQ, page);
    } catch (e) {
      alert(e.response?.data?.error || 'Bulk delete failed');
    } finally {
      setBulkDeleting(false);
    }
  }

  async function handleDeleteAllResults() {
    if (!window.confirm(
      `Delete ALL ${total.toLocaleString()} matching product${total !== 1 ? 's' : ''}?\n\nFeatures assigned to them will become unassigned. This affects every page of results and cannot be undone.`
    )) return;
    setBulkDeleting(true);
    try {
      const payload = activeQ ? { q: activeQ } : {};
      await axios.post('/products/delete-filtered', payload);
      setSelectedIds([]);
      setAllResultsSelected(false);
      gridRef.current?.api?.deselectAll();
      setPage(1);
      await fetchData('', 1);
      setActiveQ('');
      setSearchValue('');
    } catch (e) {
      alert(e.response?.data?.error || 'Delete failed');
    } finally {
      setBulkDeleting(false);
    }
  }

  const columnDefs = [
    {
      headerName: 'Product Name',
      field: 'name',
      flex: 1,
      minWidth: 200,
      checkboxSelection: true,
      headerCheckboxSelection: true,
      cellRenderer: params => (
        <button
          className="btn btn-ghost"
          style={{ padding: '2px 0', color: 'var(--accent)', fontWeight: 600 }}
          onClick={() => navigate(`/products/${params.data.id}`)}
        >
          {params.value}
        </button>
      ),
    },
    {
      headerName: 'Product Managers',
      field: 'pm_names',
      flex: 2,
      minWidth: 220,
      valueFormatter: p => p.value || '—',
    },
    {
      headerName: 'PM Count',
      field: 'pm_count',
      width: 110,
      type: 'numericColumn',
      cellRenderer: params => (
        <span style={{ color: params.value > 0 ? 'var(--info)' : 'var(--text-muted)' }}>
          {params.value}
        </span>
      ),
    },
    {
      headerName: 'Updated',
      field: 'updated_at',
      width: 150,
      valueFormatter: p => p.value ? new Date(p.value).toLocaleDateString() : '—',
    },
    {
      headerName: '',
      width: 130,
      sortable: false,
      cellRenderer: params => (
        <div style={{ display: 'flex', gap: 4 }}>
          <button
            className="btn btn-ghost"
            style={{ fontSize: 12 }}
            onClick={() => navigate(`/products/${params.data.id}/edit`)}
          >
            Edit
          </button>
          <button
            className="btn btn-ghost"
            style={{ fontSize: 12, color: 'var(--danger)' }}
            onClick={() => handleDelete(params.data.id, params.data.name)}
          >
            Delete
          </button>
        </div>
      ),
    },
  ];

  return (
    <div>
      <div className="page-header">
        <h2>Products</h2>
        <p>Manage products and their assigned product managers</p>
      </div>

      <div className="page-body">
        {/* Toolbar */}
        <div className="toolbar">
          <input
            className="form-input search-input"
            placeholder="Search by product name…"
            value={searchValue}
            onChange={e => setSearchValue(e.target.value)}
            onKeyDown={handleKeyDown}
          />
          <button className="btn btn-secondary" onClick={handleSearch}>
            <IconSearch /> Search
          </button>
          {activeQ && (
            <button className="btn btn-ghost" onClick={handleClear}
              style={{ color: 'var(--danger)' }}>
              Clear
            </button>
          )}
          <div className="spacer" />
          <button className="btn btn-primary" onClick={() => navigate('/products/new')}>
            <IconPlus /> New Product
          </button>
        </div>

        {activeQ && (
          <div style={{ marginBottom: 16, fontSize: 13, color: 'var(--text-secondary)' }}>
            Showing results for <strong>"{activeQ}"</strong>
          </div>
        )}

        {/* Bulk action bar */}
        {selectedIds.length > 0 && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
            padding: '10px 16px', marginBottom: 6,
            background: 'var(--accent-light)', border: '1px solid var(--accent)',
            borderRadius: 'var(--radius)', fontSize: 13,
          }}>
            <span style={{ color: 'var(--accent)', fontWeight: 600 }}>
              {allResultsSelected
                ? `All ${total.toLocaleString()} matching products selected`
                : `${selectedIds.length} product${selectedIds.length !== 1 ? 's' : ''} on this page selected`}
            </span>
            <button
              className="btn btn-danger"
              style={{ fontSize: 12, padding: '5px 12px' }}
              disabled={bulkDeleting}
              onClick={handleBulkDelete}
            >
              <IconTrash />
              {bulkDeleting
                ? 'Deleting…'
                : allResultsSelected
                  ? `Delete all ${total.toLocaleString()} results`
                  : `Delete ${selectedIds.length} selected`}
            </button>
            <button
              className="btn btn-ghost"
              style={{ fontSize: 12 }}
              onClick={() => { gridRef.current?.api?.deselectAll(); setSelectedIds([]); setAllResultsSelected(false); }}
            >
              Clear selection
            </button>
          </div>
        )}

        {/* Gmail-style "select all results" banner */}
        {selectedIds.length === rows.length && rows.length > 0 && total > rows.length && !allResultsSelected && (
          <div style={{
            padding: '8px 16px', marginBottom: 6, fontSize: 13,
            background: 'rgba(99,102,241,0.08)', borderRadius: 'var(--radius)',
            border: '1px solid var(--border)', color: 'var(--text-secondary)',
          }}>
            All {rows.length} products on this page are selected.{' '}
            <button
              className="btn btn-ghost"
              style={{ fontSize: 13, padding: '0 4px', color: 'var(--accent)', fontWeight: 600 }}
              onClick={() => setAllResultsSelected(true)}
            >
              Select all {total.toLocaleString()} matching products
            </button>
          </div>
        )}

        {error && <div className="error-msg">{error}</div>}

        {/* Grid */}
        <div style={{ position: 'relative' }}>
          {loading && (
            <div style={{
              position: 'absolute', inset: 0, zIndex: 10,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: 'rgba(15,23,42,0.5)', borderRadius: 'var(--radius-lg)',
            }}>
              <div className="spinner" />
            </div>
          )}
          <div
            className="ag-theme-alpine-dark"
            style={{ height: 520, width: '100%', borderRadius: 'var(--radius-lg)', overflow: 'hidden' }}
          >
            <AgGridReact
              ref={gridRef}
              rowData={rows}
              columnDefs={columnDefs}
              rowSelection="multiple"
              suppressRowClickSelection
              suppressMovableColumns
              suppressCellFocus
              rowHeight={44}
              headerHeight={40}
              defaultColDef={{ sortable: true, resizable: true }}
              onSelectionChanged={handleSelectionChanged}
              noRowsOverlayComponent={() => (
                <div className="empty-state">
                  <div className="icon">📦</div>
                  <h3>No products found</h3>
                  <p>{activeQ ? 'Try a different search term' : 'Create your first product to get started'}</p>
                </div>
              )}
            />
          </div>
        </div>

        {/* Pagination */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          marginTop: 16, fontSize: 13, color: 'var(--text-secondary)',
        }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            {total === 0
              ? 'No results'
              : `${(page - 1) * DEFAULT_LIMIT + 1}–${Math.min(page * DEFAULT_LIMIT, total)} of ${total.toLocaleString()} product${total !== 1 ? 's' : ''}`}
            {total > 0 && (
              <button
                className="btn btn-ghost"
                style={{ fontSize: 12, color: 'var(--danger)', padding: '2px 6px' }}
                disabled={bulkDeleting}
                onClick={handleDeleteAllResults}
              >
                <IconTrash /> Delete all {total.toLocaleString()} results
              </button>
            )}
          </span>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <button
              className="btn btn-secondary"
              style={{ padding: '5px 10px' }}
              disabled={page <= 1}
              onClick={() => handlePageChange(page - 1)}
            >
              <IconChevronLeft />
            </button>
            <span style={{ minWidth: 80, textAlign: 'center' }}>
              Page {page} of {pages}
            </span>
            <button
              className="btn btn-secondary"
              style={{ padding: '5px 10px' }}
              disabled={page >= pages}
              onClick={() => handlePageChange(page + 1)}
            >
              <IconChevronRight />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
